"use client";

import React, { useEffect, useRef } from "react";

interface GreenWave {
  y: number;
  progress: number;
  speed: number;
}

export const TopographicMap: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const maxWidth = 1450;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let time = 0;
    const lineSpacing = 15;
    const noiseScale = 0.015;

    // Green waves configuration
    const maxGreenWaves = 5;
    const waveSpeedMin = 3;
    const waveSpeedMax = 6;
    const waveOpacity = 1;

    const setCanvasSize = () => {
      canvas.width = Math.min(window.innerWidth, maxWidth);
      canvas.height = 550;
    };

    setCanvasSize();
    window.addEventListener("resize", setCanvasSize);

    const noise = (x: number, y: number) => {
      return (
        (Math.sin(x * 0.8 + y * 0.2 + time * 0.2) *
          Math.cos(x * 0.2 + y * 0.5) *
          1.5 +
          Math.sin((x + 30) * 0.3 + y * 0.4 + time * 0.1) * 0.8 +
          Math.sin(x * 0.1 + y * 0.6 + time * 0.15) * 0.6) *
        0.5
      );
    };

    const addGreenWave = () => {
      if (greenWaves.length < maxGreenWaves && Math.random() < 0.05) {
        const availableLines = Array.from(
          { length: Math.floor(canvas.height / lineSpacing) },
          (_, i) => i * lineSpacing
        ).filter((y) => !greenWaves.some((wave) => wave.y === y));

        if (availableLines.length > 0) {
          const randomY =
            availableLines[Math.floor(Math.random() * availableLines.length)];
          greenWaves.push({
            y: randomY,
            progress: 0,
            speed: waveSpeedMin + Math.random() * (waveSpeedMax - waveSpeedMin),
          });
        }
      }
    };

    const greenWaves: GreenWave[] = [];

    const drawTopography = () => {
      // Get the background color from CSS
      const bgColor = getComputedStyle(canvas).backgroundColor;
      ctx.fillStyle = bgColor;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Helper function to convert background color to rgba with opacity
      const getBgColorWithOpacity = (opacity: number) => {
        // Parse the background color (supports rgb, rgba, and other formats)
        const tempDiv = document.createElement('div');
        tempDiv.style.color = bgColor;
        document.body.appendChild(tempDiv);
        const computedColor = getComputedStyle(tempDiv).color;
        document.body.removeChild(tempDiv);
        
        // Extract RGB values from the computed color
        const rgbMatch = computedColor.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
        if (rgbMatch) {
          const [, r, g, b] = rgbMatch;
          return `rgba(${r}, ${g}, ${b}, ${opacity})`;
        }
        
        // Fallback to original hardcoded color if parsing fails
        return `rgba(6, 7, 8, ${opacity})`;
      };

      // Base opacity for the pattern
      const baseOpacity = 0.4;
      const centerY = canvas.height / 2;
      const maxDistance = Math.sqrt(
        Math.pow(canvas.width / 2, 2) + Math.pow(canvas.height / 2, 2)
      );

      for (let y = 0; y < canvas.height; y += lineSpacing) {
        ctx.beginPath();

        const isGreenWave = greenWaves.find((wave) => wave.y === y);

        for (let x = 0; x < canvas.width; x += 2) {
          const noiseValue = noise(x * noiseScale, y * noiseScale) * 30;
          const secondaryNoise =
            noise((x + 50) * noiseScale * 2, (y + 50) * noiseScale * 2) * 10;
          const combinedNoise = noiseValue + secondaryNoise;

          if (x === 0) {
            ctx.moveTo(x, y + combinedNoise);
          } else {
            ctx.lineTo(x, y + combinedNoise);
          }
        }

        // Calculate distance from center for radial effect
        const distanceFromCenter = Math.sqrt(Math.pow(y - centerY, 2));
        const radiusMultiplier = 1 - distanceFromCenter / maxDistance;
        const adjustedOpacity = baseOpacity * (0.5 + 0.5 * radiusMultiplier);

        if (isGreenWave) {
          const gradient = ctx.createLinearGradient(0, 0, canvas.width, 0);
          gradient.addColorStop(0, `rgba(160, 160, 160, ${adjustedOpacity})`);
          gradient.addColorStop(
            Math.max(0, Math.min(1, isGreenWave.progress - 0.1)),
            `rgba(160, 160, 160, ${adjustedOpacity})`
          );
          gradient.addColorStop(
            Math.max(0, Math.min(1, isGreenWave.progress)),
            `rgba(6, 255, 93, ${waveOpacity})`
          );
          gradient.addColorStop(
            Math.max(0, Math.min(1, isGreenWave.progress + 0.1)),
            `rgba(160, 160, 160, ${adjustedOpacity})`
          );
          gradient.addColorStop(1, `rgba(160, 160, 160, ${adjustedOpacity})`);
          ctx.strokeStyle = gradient;
        } else {
          ctx.strokeStyle = `rgba(160, 160, 160, ${adjustedOpacity})`;
        }

        ctx.stroke();
      }

      // Update green waves
      for (let i = greenWaves.length - 1; i >= 0; i--) {
        greenWaves[i].progress += greenWaves[i].speed / canvas.width;
        if (greenWaves[i].progress > 1) {
          greenWaves.splice(i, 1);
        }
      }

      // Fade overlays
      const verticalGradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
      verticalGradient.addColorStop(0, getBgColorWithOpacity(1));
      verticalGradient.addColorStop(0.2, getBgColorWithOpacity(0));
      verticalGradient.addColorStop(0.8, getBgColorWithOpacity(0));
      verticalGradient.addColorStop(1, getBgColorWithOpacity(1));
      ctx.fillStyle = verticalGradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Modified radial gradient for better edge fading
      const gradient = ctx.createRadialGradient(
        canvas.width / 2,
        canvas.height / 2,
        0,
        canvas.width / 2,
        canvas.height / 2,
        canvas.width * 0.6
      );
      gradient.addColorStop(0, getBgColorWithOpacity(0));
      gradient.addColorStop(0.7, getBgColorWithOpacity(0.4));
      gradient.addColorStop(1, getBgColorWithOpacity(1));
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Add an extra horizontal gradient for better side fading
      const horizontalGradient = ctx.createLinearGradient(
        0,
        0,
        canvas.width,
        0
      );
      horizontalGradient.addColorStop(0, getBgColorWithOpacity(1));
      horizontalGradient.addColorStop(0.1, getBgColorWithOpacity(0));
      horizontalGradient.addColorStop(0.9, getBgColorWithOpacity(0));
      horizontalGradient.addColorStop(1, getBgColorWithOpacity(1));
      ctx.fillStyle = horizontalGradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    };

    const animate = () => {
      time += 0.005;
      addGreenWave();
      drawTopography();
      requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener("resize", setCanvasSize);
    };
  }, []);

  return (
    <div className="absolute inset-x-0 top-18 z-0 h-[540px] mx-auto" style={{ maxWidth: `${maxWidth}px` }}>
      <div className="relative w-full h-full">
        <canvas
          ref={canvasRef}
          className="w-full h-full bg-background"
          style={{
            maskImage: `radial-gradient(ellipse 85% 90% at center, black 50%, transparent 85%)`,
            WebkitMaskImage: `radial-gradient(ellipse 85% 90% at center, black 50%, transparent 85%)`,
          }}
        />
        {/* Additional fade overlays for edge blending */}
        <div 
          className="absolute inset-0 pointer-events-none"
          style={{
            background: `
              linear-gradient(to right, var(--background) 0%, transparent 10%, transparent 90%, var(--background) 100%),
              linear-gradient(to bottom, var(--background) 0%, transparent 12%, transparent 88%, var(--background) 100%)
            `
          }}
        />
      </div>
    </div>
  );
};
